import { css, cx } from '@emotion/css';
import { Button, Tooltip } from '@mantine/core';
import { modals } from '@mantine/modals';
import { IconBombFilled, IconMoodNeutral } from '@tabler/icons-react';
import { useTranslation } from 'locales';
import type { ComponentProps } from 'react';
import { isDefined } from 'remeda';

import { SuperUserOnly } from './SuperUserOnly';

type SuperButtonProps = ComponentProps<typeof Button> &
  React.HTMLAttributes<HTMLButtonElement> & {
    disabledMessage?: string | null;
    modalContent?: React.ReactNode;
  };

/**
 * Responds to all the regular Button props, but guards the onClick function with a confirmation modal
 */
export const SuperButton = ({
  onClick,
  modalContent,
  disabledMessage,
  ...buttonProps
}: SuperButtonProps) => {
  const { t } = useTranslation();

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (isDefined(modalContent)) {
      modals.open({
        title: t('SUPERADMIN.CONFIRM_TITLE'),
        children: modalContent,
        overlayProps: { blur: 3, color: '#450000', opacity: 0.95 },
      });
    } else {
      areYouSure(event);
    }
  };

  const areYouSure = (event: React.MouseEvent<HTMLButtonElement>) => {
    modals.openConfirmModal({
      title: t('SUPERADMIN.CONFIRM_TITLE'),
      children: <p>{t('SUPERADMIN.CONFIRM_MESSAGE')}</p>,
      labels: { confirm: 'Yes, do it', cancel: 'No, get me out' },
      cancelProps: {
        leftSection: <IconMoodNeutral size={18} stroke={1.5} />,
      },
      confirmProps: {
        variant: 'gradient',
        className: cx(superButtonStyles.gradientHover, superButtonStyles.alwaysAnimate),
        leftSection: <IconBombFilled size={18} stroke={1.5} />,
      },
      onConfirm: () => onClick?.(event),
      overlayProps: { blur: 3, color: '#450000', opacity: 0.95 },
    });
  };

  return (
    <SuperUserOnly>
      <Tooltip label={disabledMessage} disabled={!disabledMessage} c="white" color="red">
        <Button
          {...buttonProps}
          disabled={!!disabledMessage}
          onClick={handleClick}
          leftSection={<IconBombFilled size={18} stroke={1.5} />}
          className={superButtonStyles.gradientHover}
        />
      </Tooltip>
    </SuperUserOnly>
  );
};

export const superButtonStyles = {
  gradientHover: css`
    background: linear-gradient(
      45deg,
      var(--mantine-color-red-9),
      var(--mantine-color-red-6),
      var(--mantine-color-red-9),
      var(--mantine-color-red-6),
      var(--mantine-color-red-9)
    );
    background-size: 200%;

    &:hover {
      animation: gradient-move 3s linear infinite;
    }

    @keyframes gradient-move {
      0% {
        background-position: 0% 0%;
      }
      100% {
        background-position: 300% 0%;
      }
    }
  `,
  alwaysAnimate: css`
    animation: gradient-move 3s linear infinite;
  `,
};
